import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import SpaceRepository from "@services/repositories/spaceRepository";
import PageableViewModel from "@contents/viewModels/pageableViewModel";
import {useNavigate} from "react-router-dom";
import {RouteUrl} from "@app/constants/routeUrl";
import {
    ShopCopyEntity,
    ShopCopyPayload,
    ShopCopyProgressEntity,
    SpaceBrandShopEntity,
    SpaceListGetPayload
} from "@services/entities/spaceEntity";
import {BrandListEntity} from "@services/entities/brandEntity";
import {useRecoilState, useRecoilValue} from "recoil";
import {memberState, isShopCopyState, spaceState} from "@services/stores/storeState";
import {SpaceStateSetModel} from "@contents/models/spaceModel";
import {DeviceCountByBrandEntity, DeviceListGetPayload} from "@services/entities/deviceEntity";
import {ResponseEntity, ResponseListEntity} from "@services/entities/baseEntity";
import {isEmptyString, setLocalSearchParams, setSort} from "@utils/commonUtils";
import {Strings} from "@app/constants/strings";
import {ShopCopySaveModel} from "@contents/models/commonModel";
import {KEYS} from "@app/constants/keys";
import {eAlert} from "@app/events";

const SpaceViewModel = () => {
    const spaceRepository = SpaceRepository();
    const [spaceInfoState, setSpaceInfoState] = useRecoilState(spaceState);
    const memberMeInfoState = useRecoilValue(memberState);
    const navigate = useNavigate();
    const pageable = PageableViewModel();

    const spaceList = spaceRepository.spaceListData;
    const spaceData = spaceRepository.spaceData;
    const spaceBrandPopupData = spaceRepository.spaceBrandListData?.content;
    const spaceAllList = spaceRepository.spaceAllListData?.content;
    const spaceBrandShopList = spaceRepository.spaceBrandShopAllListData?.content;
    const allSpaceBrandShopList = spaceRepository.allSpaceBrandShopListData?.content;
    const tableRef = useRef(null);
    const [spaceCode, setSpaceCode] = useState<string>('');

    const [shopCopyError, setShopCopyError] = useState<string>('');
    const isShopCopied = useRecoilValue(isShopCopyState);
    // const [shopProgressData, setShopProgressData] = useState<ShopCopyProgressEntity>({});
    const shopProgressData = spaceRepository.shopCopyProgressData?.content;
    const shopCopyProgressResult = spaceRepository.shopCopyProgressResult;

    // 스페이스 검색 항목
    const [spaceListSearch, setSpaceListSearch] = useState<SpaceListGetPayload>({
        page: pageable.page,
        size: pageable.rowPerPage,
        sort: [setSort('spaceName', 'DESC')]
    });

    // 스페이스 검색
    const getSpaceList = useCallback(() => {
        spaceRepository.getSearchSpaceList(spaceListSearch);
    }, [spaceRepository, spaceListSearch])

    const goBrandDetail = (row: BrandListEntity) => {
        navigate(RouteUrl.operation.space.space.url + '/' + row.brandCode, {
            state: {
                tab: 'brand'
            }
        })
    }

    const goBrandEdit = (brandCode?: string) => {
        brandCode && navigate(RouteUrl.operation.space.space.url + '/' + brandCode + '/brand');
    }

    const goShopEdit = (brandCode?: string, shopId?: number) => {
        brandCode && navigate(RouteUrl.operation.space.space.url + '/' + brandCode + '/shop/' + shopId);
    }

    const goShopAdd = (brandCode?: string) => {
        brandCode && navigate(RouteUrl.operation.space.space.url + '/' + brandCode + '/shop');
    }

    const goDeviceEdit = (brandCode?: string, deviceId?: number, deviceSearch?: DeviceListGetPayload) => {
        brandCode && navigate(RouteUrl.operation.space.space.url + '/' + brandCode + '/device/' + deviceId);
        deviceSearch && setLocalSearchParams(deviceSearch)
    }

    const goDeviceAdd = (brandCode?: string) => {
        brandCode && navigate(RouteUrl.operation.space.space.url + '/' + brandCode + '/device');
    }

    const getSpaceBrandPopupList = () => {
        spaceRepository.getSpaceBrandPopupList()
    }

    const getSpaceAllList = () => {
        spaceRepository.getSpaceAllList();
    }

    const getSpaceBrandShopList = async (spaceCode: string, callback?: (data: ResponseListEntity<SpaceBrandShopEntity>) => void) => {
        await spaceRepository.getSpaceBrandShopAllList({spaceCode: spaceCode, callback: callback})
    }

    // 스페이스 리스트의 모든 브랜드와 샵을 가져온다.
    const getAllSpaceBrandShopList = () => {
        spaceRepository.getAllSpaceBrandShopList();
    }

    const setSpaceUrlChange = (space: string, callback: () => void) => {
        spaceRepository.onSetSpaceName(space, callback);
    }

    // recoil spaceInfo를 셋팅한다.
    const onSpaceInfoStateSet = ({
                                     space,
                                     spaceName,
                                     brandCode,
                                     brandName,
                                     selectedShopId,
                                     selectedTopMenu
                                 }: SpaceStateSetModel, callback?: (value?) => void) => {
        setSpaceInfoState({
            ...spaceInfoState,
            space: space ?? spaceInfoState.space,
            spaceName: spaceName ?? spaceInfoState.spaceName,
            brandCode: brandCode ?? spaceInfoState.brandCode,
            brandName: brandName ?? spaceInfoState.brandName,
            selectedShopId: selectedShopId ?? spaceInfoState.selectedShopId,
            selectedTopMenu: selectedTopMenu ?? spaceInfoState.selectedTopMenu
        })
        callback && callback()
    }

    const onSpaceSet = () => {
        spaceRepository.onSpaceSetRecoil(() => {
            navigate(RouteUrl.base)
        })
    }

    const goSystemDeviceList = (row: DeviceCountByBrandEntity) => {
        navigate(RouteUrl.operation.system.remote.url + '/' + row.brandCode,
            {
                state: {
                    tab: 'brand'
                }
            })
    }

    const goSystemDistributeDeviceList = (row: BrandListEntity) => {
        navigate(RouteUrl.operation.system.distribute.url + '/' + row.brandCode, {
            state: {
                brandName: row.name
            }
        })
    }

    const onProcessShopCopy = (shopCopySaveData: ShopCopySaveModel, handelClose?: (...args: any[]) => void) => {
        setShopCopyError('');
        if(shopCopySaveData?.sourceShopId && shopCopySaveData?.targetShopId){
            spaceRepository?.onShopCopy(shopCopySaveData, {
                callback: (res: ResponseEntity<ShopCopyEntity>) => {
                    if(res?.code === 'SHOP_DATA_NOT_FOUND'){
                        setShopCopyError(Strings.shopNotExist);
                    }else if(res?.code === 'GOODS_COPY_FAIL_TOO_MANY_GOODS'){
                        setShopCopyError(Strings.shopCopyGoodsLimitExceeded);
                    }else{
                        if(res?.content?.uuid){
                            handelClose && handelClose();
                        }else{
                            setShopCopyError(Strings.alert.serverError);
                        }
                    }
                }
            });
        }
    }

    const getShopCopyProgress = () => {
        spaceRepository?.getShopCopyProgress();
    }

    const isCopying = (brandCode?: string, shopId?: string): boolean => {
        //shop 복사중 여부 체크
        const isCopyInProgress = !isEmptyString(shopCopyInfo?.uuid || '');

        if (!isCopyInProgress) {
            return false; // 복사 중이 아니면 항상 OK
        }

        //복사 중인 브랜드인지 체크
        const isBrandCopying = brandCode ?
            (brandCode === shopCopyInfo?.sourceBrandCode || brandCode === shopCopyInfo?.targetBrandCode) : false;

        //복사 중인 shop인지 체크 (shop이 없으면 복사 중이 아니다)
        const isShopCopying = shopId !== undefined ?
            (Number(shopId) === shopCopyInfo?.sourceShopId || Number(shopId) === shopCopyInfo?.targetShopId) : false;

        console.log('isShopCopying', isBrandCopying);
        console.log('isShopCopying', isShopCopying);

        if(isBrandCopying || isShopCopying){
            eAlert(Strings.alert.waitForShopCopy);
        }

        return isBrandCopying || isShopCopying;
    }

    const shopCopyInfo = useMemo(() => {
        const shopCopySavedData = localStorage.getItem(KEYS.SHOP_COPY) || '';
        if (!isEmptyString(shopCopySavedData)) {
            return (JSON.parse(shopCopySavedData) as ShopCopySaveModel);
        }else{
            return undefined;
        }
    }, []);

    return {
        tableRef,
        pageable,
        spaceList,
        spaceData,
        spaceBrandPopupData,
        spaceAllList,
        spaceBrandShopList,
        spaceInfoState,
        memberMeInfoState,
        onSpaceInfoStateSet,
        spaceListSearch,
        setSpaceListSearch,
        getSpaceList,
        goBrandDetail,
        goBrandEdit,
        setSpaceCode,
        goShopEdit,
        goShopAdd,
        goDeviceEdit,
        goDeviceAdd,
        getSpaceBrandPopupList,
        getSpaceAllList,
        getSpaceBrandShopList,
        onSpaceSet,
        goSystemDeviceList,
        goSystemDistributeDeviceList,
        setSpaceUrlChange,
        getAllSpaceBrandShopList,
        allSpaceBrandShopList,
        onProcessShopCopy,
        getShopCopyProgress,
        shopProgressData,
        shopCopyError,
        shopCopyInfo,
        isCopying,
        shopCopyProgressResult,
        isShopCopied,
        setShopCopyError
    }
}

export default SpaceViewModel;